import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { YolutapLogo } from "@/components/brand/YolutapLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { getTestProgress, saveChildProgress } from "@/lib/test-runner.functions";
import { getRuntimeChildQuestions } from "@/lib/test-content-runtime.functions";
import { shuffleAnswersForQuestion } from "@/lib/diagnostic/seeded-shuffle";
import type { LocalizedQuestion } from "@/lib/yolutap/types";
import { useT } from "@/lib/i18n";
import {
  PretestIntroScreen,
  pretestIntroKeyFor,
} from "@/components/pretest/PretestIntroScreen";
import { trackStartTest, trackTestComplete, oncePerSession } from "@/lib/analytics";
import { usePublishPageLanguage } from "@/lib/page-language";

const PALETTE = {
  bg: "#F0EDE5",
  paper: "#FBFAF7",
  cream: "#F4F1EA",
  ink: "#0F2A38",
  teal: "#1E5A6B",
  tealInk: "#143F4B",
  amber: "#F0A63A",
  line: "#E3E7EB",
  muted: "#6B8090",
};

export const Route = createFileRoute("/session/$token/test")({
  head: () => ({
    meta: [{ title: "Test — Yolutap" }, { name: "robots", content: "noindex" }],
  }),
  component: TestRunnerPage,
});

function TestRunnerPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const fetchProgress = useServerFn(getTestProgress);
  const fetchQuestions = useServerFn(getRuntimeChildQuestions);
  const saveFn = useServerFn(saveChildProgress);

  // Снимок сервера. Обновляется не дозапросом, а ответом самого сохранения:
  // saveChildProgress возвращает тот же DTO, и это единственное место, где
  // мы узнаём, что ответ доехал.
  const queryClient = useQueryClient();
  const PROGRESS_KEY = ["test-progress", token] as const;

  const { data, isLoading, error } = useQuery({
    queryKey: PROGRESS_KEY,
    queryFn: () => fetchProgress({ data: { access_token: token } }),
  });

  const t = useT(data?.language_key);
  // То же, что на отчёте: заголовки блоков печатаются в верхнем регистре, и
  // без lang="az" «i» теряет точку.
  usePublishPageLanguage(data?.language_key ?? null);

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [index, setIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Pre-test honesty screen ack (① / ② / ③ depending on age_group).
  // Shown at the START of every attempt (childAnswered === 0) — in-session
  // only, no localStorage persistence. The component falls through to
  // questions as soon as the user clicks the CTA. See
  // docs/yolutap-pretest-intro-screens-CC.md (note: behavior changed from
  // "once per token×screen" → "every attempt" per product owner decision).
  const [introAck, setIntroAck] = useState<boolean>(false);

  // Serialise background saves so a slow save can't overwrite a newer one.
  // Each click chains onto this promise; we keep advancing the UI optimistically.
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  // Latest snapshot of answers — used so the most recent save wins even if
  // older ones finish later (last-write-wins with newest full state).
  const latestAnswersRef = useRef<Record<string, string>>({});

  const { data: questionsData } = useQuery({
    queryKey: ["runtime-child-questions", data?.test_version_id, data?.language_key],
    enabled: !!data,
    queryFn: () =>
      fetchQuestions({
        data: {
          test_version_id: data!.test_version_id,
          language_key: data!.language_key as "ru" | "az" | "en",
        },
      }),
  });
  // useMemo stabilises the questions array reference while `questionsData`
  // is still undefined (2nd useQuery loading). Without this, `?? []` would
  // allocate a fresh empty array literal on every render, and the effect
  // below would see a new dependency reference every render → re-fire →
  // setAnswers(new object) → re-render → repeat until questionsData resolves.
  //
  // On desktop V8 the loop closes in <100ms once questionsData lands and no
  // user notices. On iOS WebKit stack frames are cheaper to accumulate and
  // the cap is lower — on a slow mobile connection the same loop pins the
  // main thread long enough to hit `RangeError: Maximum call stack size
  // exceeded`. Sentry issue 32779d1fbb3b45a290e6f514e1fcfc21.
  const questions = useMemo<LocalizedQuestion[]>(
    () => questionsData ?? [],
    [questionsData],
  );

  // Marketing `start_test` — the parent clicked "Начать диагностику" on the
  // session intro and landed here, so the assessment has begun. Fired once per
  // browser per session (a fresh attempt = a fresh token via /start, so the
  // per-session key naturally re-arms for genuinely new attempts). Gated off
  // pending_payment so an unpaid session that somehow reaches this route can't
  // register a start.
  useEffect(() => {
    if (!data) return;
    if (data.status === "pending_payment" || data.status === "expired") return;
    if (!oncePerSession(`start_test_${data.id}`)) return;
    trackStartTest({
      sessionId: data.id,
      ageGroup: data.age_group,
      language: data.language_key,
    });
  }, [data]);

  // test_complete — сервер поставил complete (ответ на последний вопрос сохранён). Один раз на сессию в этом браузере.
  useEffect(() => {
    if (!data || data.status !== "complete") return;
    if (!oncePerSession(`test_complete_${data.id}`)) return;
    trackTestComplete({ ageGroup: data.age_group, language: data.language_key });
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const map: Record<string, string> = {};
    for (const a of data.child_answers) map[a.question_id] = a.answer_id;
    // Merge server state with any in-flight local answers so an optimistic
    // click isn't wiped when the refetched server snapshot lags behind.
    const merged = { ...latestAnswersRef.current, ...map };
    latestAnswersRef.current = merged;
    setAnswers(merged);
    if (questions.length) {
      const firstUnanswered = questions.findIndex((q) => !merged[q.question_id]);
      setIndex(firstUnanswered === -1 ? questions.length - 1 : firstUnanswered);
    }
  }, [data, questions]);

  // Pre-test honesty intro is now SHOWN at the start of every attempt.
  // introAck stays in-session only: initial false from useState, set true
  // only after the user acks the intro in THIS page session. No
  // localStorage seen-flag, no auto-true from server-side child_answers.
  // The downstream gate `childAnswered === 0 && !introAck` is what keeps
  // mid-test reloads from re-showing the intro (1 token = 1 attempt model:
  // a new attempt = new token via /start, so a fresh token always lands
  // with childAnswered === 0 and intro fires).

  /**
   * ЧАСЫ, А НЕ ТАЙМЕР. Школа просила видеть время; отключения по времени мы
   * НЕ делаем, пока оборванная сессия не даёт отчёта вообще — таймер отнимал
   * бы работу, ничего не давая взамен.
   *
   * Отсчёт от started_at С СЕРВЕРА, а не от монтирования компонента: у
   * ребёнка, чья страница перезагрузилась — а 7 сентября это было у каждого,
   * — часы иначе показали бы свежие ноль минут, и он решил бы, что время
   * сбросилось.
   *
   * Замер того же дня: медиана 27 минут, максимум 32,4 — две цифры хватает.
   */
  const startedAt = data?.started_at ? new Date(data.started_at).getTime() : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);
  const elapsedText =
    startedAt === null
      ? null
      : (() => {
          const sec = Math.max(0, Math.floor((now - startedAt) / 1000));
          return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
        })();

  if (isLoading) return <Centered>{t("common.loading")}</Centered>;
  if (error) {
    return (
      <Centered>
        <p className="text-destructive text-sm">
          {error instanceof Error ? error.message : t("common.error")}
        </p>
      </Centered>
    );
  }
  if (!data) {
    return (
      <Centered>
        {t("common.session_not_found")}{" "}
        <Link to="/start" search={{}} className="text-primary underline">
          {t("common.create_new")}
        </Link>
      </Centered>
    );
  }

  if (data.status === "pending_payment") {
    return (
      <Centered>
        {t("test.payment_first")}{" "}
        <Link to="/session/$token" params={{ token }} className="text-primary underline">
          {t("test.to_session")}
        </Link>
      </Centered>
    );
  }

  // Use the smaller of (server-declared total, actually loaded questions) as
  // the authoritative total — otherwise a config/DB mismatch (e.g. config says
  // 60 but DB only has 29 published rows) leaves the user stranded on the
  // last reachable question with no way to finish.
  const childTotal =
    questions.length > 0
      ? Math.min(data.child_question_count, questions.length)
      : data.child_question_count;
  const childAnswered = Object.keys(answers).length;

  // «ГОТОВО» — ПО ОТВЕТУ СЕРВЕРА, А НЕ ПО СЧЁТЧИКУ В ПАМЯТИ.
  //
  // Раньше здесь стояло `childAnswered >= childTotal`, то есть число ключей
  // в состоянии React. На последнем вопросе индекс не двигается, сохранение
  // уходит в фон — и экран переключался на «Готово» в тот же кадр, ещё до
  // ответа сервера. Оборвись это сохранение (а именно оно самое длинное: на
  // нём рисуется PDF и ставится письмо в очередь), ребёнок видел бы «Готово»
  // и кнопку «Перейти к результату», а на сервере лежал бы тест без
  // последнего ответа, без статуса complete, без отчёта и без письма. Строка
  // об ошибке до него бы не дошла: она живёт ниже, в ветке вопроса.
  //
  // Теперь пока сервер не подтвердил, ребёнок остаётся на вопросе — со
  // «Сохраняем…» или с причиной и кнопкой «Повторить». Подтверждение
  // приносит ответ самого сохранения, отдельного запроса нет.
  const serverAnswered = data.child_answers.length;
  if (data.status === "complete" || serverAnswered >= childTotal) {
    return (
      <Centered>
        <Card className="max-w-md w-full">
          <CardContent className="py-8 text-center space-y-4">
            <p className="text-lg font-semibold">{t("test.child_done_title")}</p>
            <p className="text-sm text-muted-foreground">{t("test.child_done_text")}</p>
            <Button onClick={() => navigate({ to: "/session/$token/result", params: { token } })}>
              {t("test.go_result")}
            </Button>
          </CardContent>
        </Card>
      </Centered>
    );
  }

  // Pre-test honesty intro — gated on age_group. Shown once per token
  // before Q1 (childAnswered === 0). After ack, falls through to the
  // question runner immediately. This is purely a UX gate — it does NOT
  // touch the question loader, scoring path, or saveChildProgress.
  if (childAnswered === 0 && !introAck) {
    const screenKey = pretestIntroKeyFor(data.age_group);
    return (
      <PretestIntroScreen
        screenKey={screenKey}
        lang={data.language_key}
        onAck={() => {
          // Per-session ack only — no localStorage persistence so the
          // intro re-fires on every fresh attempt (new token, childAnswered=0).
          setIntroAck(true);
        }}
      />
    );
  }

  const q = questions[index];
  if (!q) return <Centered>{t("test.questions_not_loaded")}</Centered>;

  const selected = answers[q.question_id];
  const progressPct = Math.round((childAnswered / childTotal) * 100);

  // Background save — chained so older saves never overwrite newer ones.
  // We always send the LATEST full answers snapshot at the moment the chained
  // task actually runs, so a slow save in flight won't reset newer answers.
  function queueSave() {
    setSaveError(null);
    setSaving(true);
    saveQueueRef.current = saveQueueRef.current
      .catch(() => {}) // swallow prior failures so the chain continues
      .then(async () => {
        const snapshot = latestAnswersRef.current;
        const list = Object.entries(snapshot).map(([question_id, answer_id]) => ({
          question_id,
          answer_id,
        }));
        try {
          const saved = await saveFn({ data: { access_token: token, answers: list } });
          setSaveError(null);
          // ОТВЕТ СОХРАНЕНИЯ — И ЕСТЬ ПОДТВЕРЖДЕНИЕ СЕРВЕРА.
          //
          // saveChildProgress возвращает тот же снимок, что и getTestProgress:
          // статус, сохранённые ответы, started_at. Кладём его в кэш запроса —
          // и экран сразу знает то, что знает сервер. Отдельного дозапроса
          // больше нет, а был он дважды: за часами (started_at появляется
          // только с первым сохранённым ответом — найдено прогоном 7 сентября)
          // и за статусом после последнего вопроса. Оба ответа приходят сюда
          // сами, и в отличие от дозапроса этот ответ нельзя потерять молча:
          // не пришёл — значит сохранение не удалось, и это видно.
          if (saved) queryClient.setQueryData(PROGRESS_KEY, saved);
        } catch (e) {
          setSaveError(e instanceof Error ? e.message : t("test.save_failed"));
          throw e;
        }
      })
      .finally(() => {
        // Only clear the spinner once this is the most recent task in the queue.
        // We approximate by deferring to the next microtask — if another save
        // was queued after us, it will set saving=true again synchronously.
        queueMicrotask(() => setSaving(false));
      })
      // ХВОСТ ЦЕПОЧКИ. Без него отказ последнего сохранения не имел
      // обработчика до следующего клика — а его могло и не быть, если
      // сломалось как раз на последнем вопросе. Браузер объявлял
      // unhandledrejection, и обрыв связи уезжал в Sentry как необработанная
      // ошибка (issue d774b78c, Safari, 18 сентября). Причина уже показана
      // ребёнку через setSaveError — второй раз она нужна только шуму.
      .catch(() => {});
  }

  function handleSelect(answerId: string) {
    const next = { ...answers, [q.question_id]: answerId };
    latestAnswersRef.current = next;
    setAnswers(next);

    // Optimistic UI: advance immediately. The save runs in the background.
    const isLast = index >= questions.length - 1;
    if (!isLast) {
      setIndex((i) => i + 1);
    }
    // На последнем вопросе ничего особенного больше не делается: статус
    // complete приходит в ответе самого сохранения и попадает в кэш там же.
    // Прежний дозапрос после последнего ответа был вторым местом, где экран
    // мог разойтись с сервером, — и именно он раньше зажигал «Готово».
    queueSave();
  }

  return (
    <div
      className="min-h-screen py-8 px-4"
      style={{
        background: PALETTE.bg,
        color: PALETTE.ink,
        fontFamily: "'Nunito Sans', system-ui, sans-serif",
        backgroundImage:
          "radial-gradient(rgba(20, 63, 75, 0.045) 1px, transparent 1px), radial-gradient(rgba(20, 63, 75, 0.025) 1px, transparent 1px)",
        backgroundSize: "28px 28px, 56px 56px",
        backgroundPosition: "0 0, 14px 14px",
      }}
    >
      <div className="mx-auto max-w-2xl">
        <header className="mb-6 flex items-center justify-between">
          <a href="/" className="inline-flex">
            <YolutapLogo height={36} />
          </a>
          <span
            className="text-xs flex items-center gap-2"
            style={{ color: PALETTE.muted }}
            aria-live="polite"
          >
            {saving && (
              <span
                className="animate-pulse"
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: PALETTE.amber,
                }}
                aria-hidden
              />
            )}
            {childAnswered} / {childTotal}
            {elapsedText && (
              <span style={{ opacity: 0.75 }}>
                · {elapsedText} {t("test.elapsed")}
              </span>
            )}
          </span>
        </header>

        <Progress value={progressPct} className="mb-6" style={{ background: PALETTE.cream }} />

        {/* translate="no": вопросы уже на языке теста. 19.10.2026 Safari на
            iPhone «перевёл» азербайджанский тест: слова переехали в метку
            варианта («A. qrupa»), три варианта опустели, формулировки
            сломались — ответ ребёнка перестал означать то, что считает отчёт. */}
        {q.block_title && (
          <p
            translate="no"
            className="text-xs uppercase tracking-[0.2em] mb-2"
            style={{ color: PALETTE.muted }}
          >
            {q.block_title}
          </p>
        )}

        <div
          translate="no"
          className="rounded-2xl p-6 shadow-sm"
          style={{ background: PALETTE.paper, border: `1px solid ${PALETTE.line}` }}
        >
          <h2
            className="text-lg leading-snug font-semibold mb-4"
            style={{ color: PALETTE.tealInk }}
          >
            {q.display_number}. {q.question_text}
          </h2>
          {q.question_type === "scale" ? (
            // Path B (18-22) — Likert 5-point row with pole labels on either
            // side. Selecting a number commits as answer_id "1".."5" via the
            // same handleSelect path.
            <div>
              <div
                className="flex items-center justify-between mb-3"
                style={{ color: PALETTE.muted, fontSize: 12 }}
              >
                <span style={{ maxWidth: "40%", lineHeight: 1.3 }}>{q.scale_poles?.low ?? ""}</span>
                <span style={{ maxWidth: "40%", textAlign: "right", lineHeight: 1.3 }}>
                  {q.scale_poles?.high ?? ""}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                {q.answers.map((a) => {
                  const isSelected = selected === a.answer_id;
                  return (
                    <button
                      key={a.answer_id}
                      type="button"
                      onClick={() => handleSelect(a.answer_id)}
                      aria-label={`${q.scale_poles?.low ?? ""} ↔ ${q.scale_poles?.high ?? ""}: ${a.answer_id}`}
                      className="flex-1 rounded-lg py-3 text-center font-bold transition"
                      style={{
                        background: isSelected ? PALETTE.teal : PALETTE.paper,
                        color: isSelected ? "#fff" : PALETTE.tealInk,
                        border: `1px solid ${isSelected ? PALETTE.teal : PALETTE.line}`,
                        fontSize: 16,
                      }}
                    >
                      {a.answer_id}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <ShuffledAnswerList
              q={q}
              selected={selected}
              token={token}
              onSelect={handleSelect}
            />
          )}
        </div>

        {saveError && (
          <div className="mt-4 text-center space-y-2">
            <p className="text-sm text-destructive">{saveError}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => queueSave()}
            >
              {t("common.retry") ?? "Повторить"}
            </Button>
          </div>
        )}

        <div className="mt-6 flex justify-between">
          <Button
            variant="ghost"
            disabled={index === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
          >
            ← {t("common.back")}
          </Button>
          <Button
            variant="ghost"
            disabled={index >= questions.length - 1 || !selected}
            onClick={() => setIndex((i) => Math.min(questions.length - 1, i + 1))}
          >
            {t("common.next")} →
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * ABCD-answer list with session-stable shuffled option order.
 *
 * Fixes the INSTRUMENT-POSA-BIAS family (notes-method 2026-06-16):
 * the question banks ship with archetype A holding 43–50% of option-A
 * positions across every band — a child clicking "A" without reading
 * is 4–5× more likely to be scored Аналитик than uniform expectation
 * predicts. The render-time shuffle, seeded by (token, question_id),
 * neutralizes that without touching the question content.
 *
 * Properties:
 *  - Deterministic per (token, question_id) → "Back" preserves order.
 *  - Different sessions see different shuffles.
 *  - Scoring side effect: NONE. calculateResult reads `archetype_code`
 *    on the chosen answer; position is purely presentational.
 *
 * Scale (1..5) questions skip this component — Likert order is part of
 * the scale semantics. See the parent branch in the if/else above.
 */
function ShuffledAnswerList({
  q,
  selected,
  token,
  onSelect,
}: {
  q: LocalizedQuestion;
  selected: string | undefined;
  token: string;
  onSelect: (answer_id: string) => void;
}) {
  const shuffled = useMemo(
    () => shuffleAnswersForQuestion(q.answers, token, q.question_id),
    [q.answers, q.question_id, token],
  );
  return (
    <div className="space-y-2">
      {shuffled.map((a) => {
        const isSelected = selected === a.answer_id;
        return (
          <button
            key={a.answer_id}
            type="button"
            onClick={() => onSelect(a.answer_id)}
            className="w-full text-left rounded-lg p-4 transition"
            style={{
              background: isSelected ? PALETTE.cream : PALETTE.paper,
              border: `1px solid ${isSelected ? PALETTE.teal : PALETTE.line}`,
              color: PALETTE.ink,
            }}
          >
            <span className="font-semibold mr-2" style={{ color: PALETTE.amber }}>
              {a.option_label}.
            </span>
            {a.text}
          </button>
        );
      })}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 text-center"
      style={{
        background: PALETTE.bg,
        color: PALETTE.ink,
        fontFamily: "'Nunito Sans', system-ui, sans-serif",
      }}
    >
      <div className="space-y-3">{children}</div>
    </div>
  );
}
